import { forwardRef, useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import type { Assignee, ApprovalState, ContentTag, StandardTag } from '@cg-projects/shared';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useEscapeToClose } from './useEscapeToClose';
import { initials, avatarColor, tagColor } from './helpers';

export function Avatar({ person, size = 22 }: { person: Assignee | { id: number; name: string; avatarUrl?: string | null }; size?: number }) {
  // The lookup map is the FRESHEST source (it refetches after photo uploads);
  // task payloads bake avatarUrl at fetch time and go stale on replacement.
  // Surfaces that send avatarUrl: null (members, watchers, matrix) resolve too.
  const resolved = useLookupStore((s) => s.assignees.find((a) => a.id === person.id)?.avatarUrl ?? null);
  const url = resolved ?? person.avatarUrl ?? null;
  // Fall back to initials if the avatar image fails to load — the server now
  // sets avatarUrl for everyone and returns 404 for users with no photo, so this
  // graceful degradation is what shows their initials.
  const [failed, setFailed] = useState(false);
  // Reset when the URL changes: this component instance is reused for a DIFFERENT
  // person (Settings list) or the SAME person after a photo upload (cache-busted
  // URL) — without this, one 404 latches initials forever on the reused fiber.
  useEffect(() => { setFailed(false); }, [url]);
  const style = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={person.name}
        title={person.name}
        onError={() => setFailed(true)}
        className="rounded-full object-cover ring-1 ring-page"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      title={person.name}
      className="inline-flex items-center justify-center rounded-full font-medium text-white ring-1 ring-page"
      style={{ ...style, backgroundColor: avatarColor(person.name) }}
    >
      {initials(person.name)}
    </span>
  );
}

export function AssigneeStack({ assignees, max = 3 }: { assignees: Assignee[]; max?: number }) {
  if (assignees.length === 0) return null;
  const shown = assignees.slice(0, max);
  const extra = assignees.length - shown.length;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((a) => (
        <Avatar key={a.id} person={a} />
      ))}
      {extra > 0 && (
        <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-hover px-1 text-[10px] font-medium text-muted ring-1 ring-page">
          +{extra}
        </span>
      )}
    </div>
  );
}

export function TagChip({ tag }: { tag: ContentTag | StandardTag }) {
  const c = tagColor(tag.color);
  return (
    <span
      className="inline-flex max-w-[140px] items-center truncate rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {tag.name}
    </span>
  );
}

// Tinted chip + tinted text, resolved per theme: status states ride the
// index.css tokens; the two states without a token (neutral In-Progress,
// muted-red Canceled) ride palette.css pairs.
const STATE_META: Record<ApprovalState, { label: string; bg: string; fg: string }> = {
  '01_in_progress': { label: 'In Progress', bg: 'var(--chip-neutral-bg)', fg: 'var(--chip-neutral-ink)' },
  '02_changes_requested': { label: 'Changes', bg: 'var(--color-warn-soft)', fg: 'var(--color-warn)' },
  '03_approved': { label: 'Approved', bg: 'var(--color-success-soft)', fg: 'var(--color-success)' },
  '04_waiting_normal': { label: 'Stuck', bg: 'var(--color-danger-soft)', fg: 'var(--color-danger)' },
  '1_done': { label: 'Done', bg: 'var(--color-success-soft)', fg: 'var(--color-success)' },
  '1_canceled': { label: 'Canceled', bg: 'var(--chip-red-bg)', fg: 'var(--chip-red-ink)' },
};

export function StateBadge({ state }: { state: ApprovalState }) {
  const m = STATE_META[state] ?? STATE_META['01_in_progress'];
  return (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

export function KanbanDot({ state }: { state: 'normal' | 'done' | 'blocked' }) {
  const color = state === 'done' ? 'var(--color-success)' : state === 'blocked' ? 'var(--chip-red-ink)' : 'var(--dot-idle)';
  return <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} title={state} />;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted">
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line border-t-brand" />
      {label}
    </div>
  );
}

/** A failed load, said out loud — with a way out.
 *
 *  WHY: Table/Calendar/Inbox used to render their EMPTY state when a fetch failed,
 *  so a network blip read as "you have no tasks" — work apparently gone, no retry,
 *  no explanation. An empty state and a broken state must never look the same.
 *  Every list that can fail should render this instead of its empty copy. */
export function LoadError({ message, onRetry }: { message?: string | null; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      <div className="text-sm text-danger">{message || "Couldn't load this — the connection or the server hiccuped."}</div>
      <div className="text-xs text-faint">Your work is safe; this screen just didn't load.</div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-1 rounded-md border border-line px-3 py-1 text-xs font-medium text-ink transition hover:border-brand hover:text-brand"
        >
          ↻ Try again
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* System atoms — DESIGN_DIRECTION §3d. The login screen's press       */
/* physics become the app-wide default: solid buttons brighten and     */
/* compress on press; ghosts stay quiet. One disabled law everywhere:  */
/* disabled:opacity-50 — never the opacity-30/40 one-offs.             */
/* ------------------------------------------------------------------ */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerSolid';
export type ButtonSize = 'sm' | 'md' | 'lg';

// rounded-* lives in the SIZE map (not the base) so lg's rounded-xl never has
// to fight a base rounded-md — two radius utilities on one element leave the
// winner to stylesheet order, which is exactly the ambiguity that bites later.
const BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 font-medium transition-all duration-150 select-none disabled:opacity-50 disabled:pointer-events-none';
const BTN_SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 rounded-md px-2.5 text-xs',
  md: 'h-8 rounded-md px-3 text-sm',
  lg: 'h-12 rounded-xl px-4 text-sm',
};
const BTN_VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand font-semibold text-page hover:brightness-110 active:scale-[0.98]',
  secondary: 'border border-line text-ink hover:bg-hover',
  ghost: 'text-muted hover:bg-hover hover:text-ink', // no scale — ghosts stay quiet
  danger: 'text-danger hover:bg-danger/10',
  dangerSolid: 'bg-danger-solid text-white hover:brightness-110 active:scale-[0.98]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Defaults to the quiet 'secondary' — gold is opt-in, per the one-gold rule (§3b). */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional leading glyph (an <Icon/> once icons.tsx lands). Swapped for the spinner while loading. */
  icon?: ReactNode;
  /** 12px spinner in the icon slot; the label stays so the width holds. Sets aria-busy and blocks clicks. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, loading = false, className = '', children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Default to type="button": most of these live outside <form>s, but the few
      // inside one must opt IN to submitting — accidental submits eat drafts.
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${BTN_BASE} ${BTN_SIZE[size]} ${BTN_VARIANT[variant]} ${className}`}
      {...rest}
    >
      {loading ? (
        <span aria-hidden className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});

/* Fields — ONE recipe for every text control in the app. Exported as classes
   (not only components) because plenty of call-sites are native <select>s or
   date inputs that can't take a wrapper — they wear the same skin by class. */
const FIELD_CORE =
  'w-full rounded-md border border-line bg-canvas px-2.5 text-ink outline-none transition placeholder:text-faint focus:border-brand focus:ring-1 focus:ring-brand/60';
export const fieldClass = `h-8 text-sm ${FIELD_CORE}`;
/** Dense variant — filter bars, inline table editors. */
export const fieldClassSm = `h-7 text-xs ${FIELD_CORE}`;

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** 'sm' = the dense h-7 recipe. (Named fieldSize because native `size` is taken.) */
  fieldSize?: 'sm' | 'md';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { fieldSize = 'md', className = '', ...rest },
  ref,
) {
  return <input ref={ref} className={`${fieldSize === 'sm' ? fieldClassSm : fieldClass} ${className}`} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className = '', ...rest }, ref) {
    // Same skin, no fixed height — a textarea owns its height via rows/className.
    return <textarea ref={ref} className={`min-h-16 py-1.5 text-sm ${FIELD_CORE} ${className}`} {...rest} />;
  },
);

/** Keyboard-hint chip. Brand gold on purpose — Kbd stays brand under §3b. */
export function Kbd({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <kbd className={`rounded bg-hover px-1.5 py-0.5 text-2xs font-medium text-brand ${className}`}>{children}</kbd>;
}

/** Loading ghost — shape it with className (h-24, w-40, …). Replaces full-panel
 *  spinners anywhere the layout is known before the data is. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden className={`animate-pulse rounded-md bg-hover ${className}`} />;
}

export type CountPillTone = 'neutral' | 'danger' | 'warn' | 'brand';
const COUNT_TONE: Record<CountPillTone, string> = {
  neutral: 'bg-hover text-muted',
  danger: 'bg-danger-soft text-danger',
  warn: 'bg-warn-soft text-warn',
  brand: 'bg-brand/15 text-brand',
};

/** Small count badge (unread, overdue, "N selected"). Tabular so 9→10 doesn't wiggle. */
export function CountPill({
  value,
  tone = 'neutral',
  className = '',
}: {
  value: number | string;
  tone?: CountPillTone;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex h-4 min-w-4 items-center justify-center rounded px-1.5 text-2xs font-medium tabular-nums ${COUNT_TONE[tone]} ${className}`}
    >
      {value}
    </span>
  );
}

/** Nothing here — and that's fine. Quiet muted/faint voice, deliberately
 *  DISTINCT from LoadError's danger voice above: an empty state and a broken
 *  state must never look the same. `icon` takes a rendered node (e.g.
 *  <Icon i={Inbox} size={20} />) so this file stays decoupled from icons.tsx;
 *  the wrapper paints it text-faint via currentColor. */
export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      {icon && <div className="text-faint">{icon}</div>}
      <div className="text-sm text-muted">{title}</div>
      {hint && <div className="text-xs text-faint">{hint}</div>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/** Anchored popover panel — THE positioning recipe for dropdowns and context
 *  menus (§3d). position:fixed on purpose: ancestors with overflow-x-auto CLIP
 *  absolutely-positioned children (the bug Layout's project menu solved), and a
 *  right-click can land anywhere. Horizontal clamp keeps the panel 8px inside
 *  the viewport (the proven math from Layout.tsx:229 / FilterBar.tsx:221);
 *  vertical clamp measures the RENDERED panel — a menu opened near the bottom
 *  edge slides up to fit instead of running off screen. Scrim z-40 / panel z-50
 *  per the z contract (dropdown 30 / scrim 40 / modal 50 / toast 60). */
export function Menu({
  anchor,
  width = 224,
  onClose,
  children,
}: {
  /** Viewport coords: `top` plus either `left` (context menus, left-aligned
   *  triggers) or `right` (right-aligned triggers; wins when both are given). */
  anchor: { top: number; left?: number; right?: number };
  width?: number;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [top, setTop] = useState(anchor.top);
  // Clamp to the viewport, and RE-clamp whenever the panel grows after mount
  // (an inline submenu expanding) — observed directly rather than leaning on
  // every-render effects, which the lint gate rightly flags as a loop hazard.
  useLayoutEffect(() => {
    const clamp = () => {
      const h = panelRef.current?.offsetHeight ?? 0;
      setTop(Math.max(8, Math.min(anchor.top, window.innerHeight - h - 8)));
    };
    clamp();
    const el = panelRef.current;
    const ro = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(clamp) : null;
    if (ro && el) ro.observe(el);
    return () => ro?.disconnect();
  }, [anchor.top]);
  // fromInputs:true = capture + stopPropagation: the menu is the topmost layer,
  // so its Esc must not also close the overlay (MyWork, task panel) beneath it.
  useEscapeToClose(onClose, { fromInputs: true });
  const style: CSSProperties = { position: 'fixed', top, width };
  if (anchor.right !== undefined) style.right = Math.max(8, Math.min(anchor.right, window.innerWidth - width - 8));
  else style.left = Math.max(8, Math.min(anchor.left ?? 8, window.innerWidth - width - 8));
  return (
    <>
      {/* Any click outside lands on the scrim and closes; a RIGHT-click outside
          closes too, instead of stacking the browser menu over a stale panel. */}
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={panelRef}
        role="menu"
        style={style}
        className="z-50 max-h-[70vh] overflow-y-auto rounded-lg border border-line bg-canvas py-1 shadow-popover animate-fade-in"
      >
        {children}
      </div>
    </>
  );
}

/** One row of a Menu — h-8 px-3 text-sm per §3d. `danger` rows keep the danger
 *  voice quiet at rest and speak on hover. `kbd` renders a right-edge Kbd chip;
 *  `trailing` is the same slot for anything else (a submenu chevron). `icon`
 *  takes a rendered node (e.g. <Icon i={Archive} size={14} />) so this file
 *  stays decoupled from icons.tsx. */
export function MenuItem({
  icon,
  label,
  kbd,
  danger = false,
  disabled = false,
  trailing,
  onSelect,
}: {
  icon?: ReactNode;
  label: ReactNode;
  kbd?: string;
  danger?: boolean;
  disabled?: boolean;
  trailing?: ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={`flex h-8 w-full items-center gap-2 px-3 text-left text-sm transition disabled:pointer-events-none disabled:opacity-50 ${
        danger ? 'text-danger hover:bg-danger/10' : 'text-ink hover:bg-hover'
      }`}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {kbd && <Kbd>{kbd}</Kbd>}
      {trailing}
    </button>
  );
}
